import { useEffect, useRef, useState } from 'react';
import { Przycisk } from './ui';

/**
 * Pole podpisu odręcznego.
 *
 * Rysowane zdarzeniami wskaźnika (`pointer*`), a nie dotykowymi — jedno API obsługuje
 * palec, rysik i mysz, więc podpis działa tak samo na telefonie serwisanta i na tablecie
 * w warsztacie. `touch-action: none` jest tu konieczne: bez tego przeciągnięcie palcem
 * przewija stronę zamiast rysować.
 *
 * Obraz oddajemy jako PNG w `data:`. Podpis bez treści, pod którą stoi, nic nie znaczy —
 * skrót treści dolicza serwer przy zapisie protokołu (docs/model-danych.md §3.5).
 */
export function PolePodpisu({
  wartosc,
  onZmiana,
  wysokosc = 180,
}: {
  wartosc: string | null;
  onZmiana: (obraz: string | null) => void;
  wysokosc?: number;
}) {
  const plotno = useRef<HTMLCanvasElement>(null);
  const rysuje = useRef(false);
  const [pusty, setPusty] = useState(!wartosc);

  // Płótno musi mieć rozmiar w pikselach urządzenia, inaczej kreska na telefonie
  // wychodzi rozmyta i przesunięta względem palca.
  useEffect(() => {
    const canvas = plotno.current;
    if (!canvas) return;

    const skala = window.devicePixelRatio || 1;
    const szerokosc = canvas.clientWidth;
    canvas.width = szerokosc * skala;
    canvas.height = wysokosc * skala;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.scale(skala, skala);
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = getComputedStyle(document.documentElement)
      .getPropertyValue('--n-900')
      .trim();
  }, [wysokosc]);

  function punkt(zdarzenie: React.PointerEvent<HTMLCanvasElement>): { x: number; y: number } {
    const prostokat = zdarzenie.currentTarget.getBoundingClientRect();
    return { x: zdarzenie.clientX - prostokat.left, y: zdarzenie.clientY - prostokat.top };
  }

  function zacznij(zdarzenie: React.PointerEvent<HTMLCanvasElement>): void {
    const ctx = plotno.current?.getContext('2d');
    if (!ctx) return;
    zdarzenie.currentTarget.setPointerCapture(zdarzenie.pointerId);
    rysuje.current = true;
    const { x, y } = punkt(zdarzenie);
    ctx.beginPath();
    ctx.moveTo(x, y);
  }

  function ciagnij(zdarzenie: React.PointerEvent<HTMLCanvasElement>): void {
    if (!rysuje.current) return;
    const ctx = plotno.current?.getContext('2d');
    if (!ctx) return;
    const { x, y } = punkt(zdarzenie);
    ctx.lineTo(x, y);
    ctx.stroke();
  }

  function skoncz(): void {
    if (!rysuje.current) return;
    rysuje.current = false;
    const canvas = plotno.current;
    if (!canvas) return;
    setPusty(false);
    onZmiana(canvas.toDataURL('image/png'));
  }

  function wyczysc(): void {
    const canvas = plotno.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setPusty(true);
    onZmiana(null);
  }

  return (
    <div>
      <div className="relative border border-n-300 bg-surface">
        <canvas
          ref={plotno}
          onPointerDown={zacznij}
          onPointerMove={ciagnij}
          onPointerUp={skoncz}
          onPointerLeave={skoncz}
          onPointerCancel={skoncz}
          style={{ width: '100%', height: wysokosc, touchAction: 'none', display: 'block' }}
        />
        {pusty && (
          <span className="t-small pointer-events-none absolute inset-0 flex items-center justify-center text-n-300">
            Podpis klienta — narysuj palcem
          </span>
        )}
        {/* Linia jak na papierowym druku: podpis stoi na czymś. */}
        <span className="pointer-events-none absolute right-14 bottom-22 left-14 border-b border-n-100" />
      </div>

      <div className="mt-7 flex items-center gap-13">
        <Przycisk type="button" onClick={wyczysc} disabled={pusty}>
          Wyczyść
        </Przycisk>
        <span className="t-small text-n-500">
          {pusty ? 'Bez podpisu zlecenia nie da się zamknąć.' : 'Podpis gotowy.'}
        </span>
      </div>
    </div>
  );
}
